import { useEffect, useState } from "react";
import { API, api } from "@/lib/api";
import { LICENSE_STATUS } from "@/lib/format";
import { Loader2 } from "lucide-react";

export const Badge = ({ cls, children, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-semibold ${cls}`}>
    {children}
  </span>
);

export const LicenseStatusBadge = ({ status, testid }) => (
  <Badge cls={LICENSE_STATUS[status]?.cls} testid={testid}>{LICENSE_STATUS[status]?.label || status}</Badge>
);

export const PageHeader = ({ eyebrow, title, subtitle, children }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between fade-up">
    <div>
      {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
      <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" data-testid="page-title">{title}</h1>
      {subtitle && <p className="mt-1 max-w-2xl text-sm text-slate-600">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap gap-2">{children}</div>}
  </div>
);

export const NativeSelect = ({ value, onChange, children, testid, className = "", ...rest }) => (
  <select
    data-testid={testid}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={`h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 shadow-sm transition-colors focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20 ${className}`}
    {...rest}
  >
    {children}
  </select>
);

export const Field = ({ label, children, hint }) => (
  <label className="flex flex-col gap-1.5 text-sm">
    <span className="font-medium text-slate-700">{label}</span>
    {children}
    {hint && <span className="text-xs text-slate-500">{hint}</span>}
  </label>
);

export const Loading = ({ label = "Caricamento…" }) => (
  <div className="flex items-center gap-2 py-16 text-slate-500" data-testid="loading-indicator">
    <Loader2 className="h-5 w-5 animate-spin" /> {label}
  </div>
);

export const Empty = ({ children }) => (
  <div className="rounded-xl border border-dashed border-slate-300 bg-white/60 px-6 py-12 text-center text-sm text-slate-500" data-testid="empty-state">
    {children}
  </div>
);

export function AthletePhoto({ athleteId, hasPhoto, name = "", className = "h-12 w-12", version = 0, rounded = "rounded-lg" }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [athleteId, version]);
  const initials = name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
  if (!hasPhoto || failed || !athleteId) {
    return (
      <div className={`${className} ${rounded} flex shrink-0 items-center justify-center bg-slate-200 font-display font-semibold text-slate-600`} data-testid="athlete-photo-placeholder">
        {initials || "?"}
      </div>
    );
  }
  return <img src={`${API}/athletes/${athleteId}/photo?v=${version}`} alt={name} onError={() => setFailed(true)}
    className={`${className} ${rounded} shrink-0 object-cover`} data-testid="athlete-photo" />;
}

export function useSettings() {
  const [s, setS] = useState({ has_logo: false });
  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
    const h = () => api.get("/settings").then((r) => setS(r.data)).catch(() => {});
    window.addEventListener("settings:changed", h);
    return () => window.removeEventListener("settings:changed", h);
  }, []);
  return s;
}

export function Logo({ settings, className = "h-10 w-10", dark = false }) {
  if (settings?.has_logo) {
    return <img src={`${API}/settings/logo?v=${settings.logo_updated_at || ""}`} alt="Logo MSP Italia" className={`${className} object-contain`} data-testid="msp-logo" />;
  }
  return (
    <div className={`${className} flex items-center justify-center rounded-lg font-display text-xs font-black tracking-tight ${dark ? "bg-white text-[#0F2042]" : "bg-[#0F2042] text-white"}`} data-testid="msp-logo-placeholder">
      MSP
    </div>
  );
}
